import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { PageHeader } from '@/components/ui-bits';
import { Mail, Save, Send, Inbox, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { fmtRelative } from '@/lib/format';

const PROVIDERS = ['generic', 'sendgrid', 'mailgun', 'ses', 'resend', 'postmark', 'brevo'];

export default function EmailPage() {
  const [cfg, setCfg] = useState(null);
  const [presets, setPresets] = useState({});
  const [form, setForm] = useState({
    provider: 'generic', host: '', port: 587, username: '', password: '', from_email: '', use_tls: true, active: true,
    imap_enabled: false, imap_host: '', imap_port: 993, imap_username: '', imap_password: '', imap_use_ssl: true, imap_folder: 'INBOX',
  });
  const [send, setSend] = useState({ to_email: '', subject: '', body_html: '' });
  const [inbox, setInbox] = useState([]);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get('/email/smtp');
    setCfg(data.smtp);
    setPresets(data.presets || {});
    if (data.smtp) setForm((prev) => ({ ...prev, ...data.smtp, password: '', imap_password: '' }));
  }, []);

  const loadInbox = useCallback(async () => {
    try {
      const { data } = await api.get('/email/inbox?limit=40');
      setInbox(data.messages || []);
    } catch {
      setInbox([]);
    }
  }, []);

  useEffect(() => { load(); loadInbox(); }, [load, loadInbox]);

  const applyPreset = (p) => {
    const preset = presets[p];
    if (!preset) return;
    setForm({ ...form, provider: p, host: preset.host, port: preset.port, use_tls: preset.use_tls });
  };

  const save = async () => {
    try {
      await api.put('/email/smtp', form);
      toast.success('SMTP/IMAP guardado');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  const sendNow = async () => {
    try {
      const { data } = await api.post('/email/send', send);
      if (data.result.sent) {
        toast.success('Email enviado', { description: data.result.used_fallback ? 'usando SMTP fallback' : 'usando SMTP del tenant' });
        loadInbox();
      } else toast.error('No se pudo enviar', { description: data.result.reason });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  const syncImap = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post('/email/imap/sync');
      toast.success(`Sincronizado: ${data.new_messages || 0} nuevos`);
      loadInbox();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error IMAP');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Email"
        description="SMTP salida + IMAP entrada. Los correos entrantes se ligan a leads (o crean uno nuevo)."
        actions={form.imap_enabled ? (
          <button className="btn-ghost" onClick={syncImap} disabled={syncing}>
            <RefreshCw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} /> Sincronizar IMAP
          </button>
        ) : null}
      />
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="surface p-5">
          <h2 className="font-display font-semibold flex items-center gap-2"><Mail className="h-4 w-4" /> SMTP (salida)</h2>
          <div className="text-xs text-muted-foreground mt-1">Si no configuras, se usa el SMTP global del SuperAdmin.</div>
          <div className="mt-3 grid gap-3">
            <div className="flex flex-wrap gap-1">
              {PROVIDERS.map((p) => (
                <button key={p} data-testid={`smtp-preset-${p}`} onClick={() => applyPreset(p)} className={`chip hover:bg-accent ${form.provider === p ? 'bg-accent text-foreground' : ''}`}>{p}</button>
              ))}
            </div>
            <label>Host<input value={form.host} onChange={(e) => setForm({ ...form, host: e.target.value })} className="input mt-1" /></label>
            <div className="grid grid-cols-2 gap-2">
              <label>Puerto<input type="number" value={form.port} onChange={(e) => setForm({ ...form, port: Number(e.target.value) })} className="input mt-1" /></label>
              <label className="flex items-end gap-2"><input type="checkbox" checked={form.use_tls} onChange={(e) => setForm({ ...form, use_tls: e.target.checked })} /> Usar TLS</label>
            </div>
            <label>Usuario<input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} className="input mt-1" /></label>
            <label>Contraseña<input type="password" placeholder={cfg ? '•••• (dejar vacío para no cambiar)' : '••••'} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="input mt-1" /></label>
            <label>Email de salida (From)<input type="email" value={form.from_email} onChange={(e) => setForm({ ...form, from_email: e.target.value })} className="input mt-1" /></label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Activo</label>

            <div className="border-t border-border pt-3 mt-1">
              <h3 className="font-medium text-sm flex items-center gap-2"><Inbox className="h-4 w-4" /> IMAP (entrada)</h3>
              <p className="text-[11px] text-muted-foreground mt-1">El worker revisa cada ~90s si está habilitado. Gmail: usa contraseña de aplicación + imap.gmail.com:993.</p>
              <label className="flex items-center gap-2 text-sm mt-2">
                <input type="checkbox" checked={!!form.imap_enabled} onChange={(e) => setForm({ ...form, imap_enabled: e.target.checked })} />
                Habilitar recepción IMAP
              </label>
              {form.imap_enabled && (
                <div className="mt-2 space-y-2">
                  <label className="block text-xs">IMAP host<input className="input mt-1" value={form.imap_host || ''} onChange={(e) => setForm({ ...form, imap_host: e.target.value })} placeholder="imap.gmail.com" /></label>
                  <div className="grid grid-cols-2 gap-2">
                    <label className="block text-xs">Puerto<input type="number" className="input mt-1" value={form.imap_port || 993} onChange={(e) => setForm({ ...form, imap_port: Number(e.target.value) })} /></label>
                    <label className="flex items-end gap-2 text-xs"><input type="checkbox" checked={form.imap_use_ssl !== false} onChange={(e) => setForm({ ...form, imap_use_ssl: e.target.checked })} /> SSL</label>
                  </div>
                  <label className="block text-xs">Usuario IMAP<input className="input mt-1" value={form.imap_username || ''} onChange={(e) => setForm({ ...form, imap_username: e.target.value })} placeholder="igual que SMTP si vacío" /></label>
                  <label className="block text-xs">Contraseña IMAP<input type="password" className="input mt-1" value={form.imap_password || ''} onChange={(e) => setForm({ ...form, imap_password: e.target.value })} placeholder="vacío = misma SMTP o sin cambiar" /></label>
                  <label className="block text-xs">Carpeta<input className="input mt-1" value={form.imap_folder || 'INBOX'} onChange={(e) => setForm({ ...form, imap_folder: e.target.value })} /></label>
                  {cfg?.imap_last_sync_at && (
                    <p className="text-[11px] text-muted-foreground">Última sync: {fmtRelative(cfg.imap_last_sync_at)} · UID {cfg.imap_last_uid || 0}</p>
                  )}
                </div>
              )}
            </div>

            <button data-testid="smtp-save" onClick={save} className="btn-primary"><Save className="h-4 w-4" /> Guardar</button>
          </div>
        </div>

        <div className="space-y-4">
          <div className="surface p-5">
            <h2 className="font-display font-semibold flex items-center gap-2"><Send className="h-4 w-4" /> Enviar correo rápido</h2>
            <div className="mt-3 space-y-3">
              <label>Para<input type="email" value={send.to_email} onChange={(e) => setSend({ ...send, to_email: e.target.value })} className="input mt-1" /></label>
              <label>Asunto<input value={send.subject} onChange={(e) => setSend({ ...send, subject: e.target.value })} className="input mt-1" /></label>
              <label>HTML<textarea value={send.body_html} onChange={(e) => setSend({ ...send, body_html: e.target.value })} className="input min-h-[120px] mt-1 font-mono text-xs" /></label>
              <button data-testid="email-send-btn" onClick={sendNow} className="btn-primary"><Send className="h-4 w-4" /> Enviar</button>
            </div>
          </div>
          <div className="surface p-5">
            <h2 className="font-display font-semibold mb-3">Buzón reciente</h2>
            {inbox.length === 0 && <p className="text-sm text-muted-foreground">Sin mensajes de email aún. Activa IMAP o envía uno.</p>}
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {inbox.map((m) => (
                <div key={m.id} className="text-sm border-b border-border/50 py-2">
                  <div className="flex justify-between gap-2">
                    <span className={`text-[10px] uppercase ${m.direction === 'in' ? 'text-primary' : 'text-muted-foreground'}`}>
                      {m.direction === 'in' ? '← In' : '→ Out'}
                    </span>
                    <span className="text-[10px] text-muted-foreground">{fmtRelative(m.created_at)}</span>
                  </div>
                  <div className="font-medium truncate">
                    {m.lead ? (
                      <Link to={`/app/leads/${m.lead.id}`} className="hover:underline">
                        {m.lead.first_name} {m.lead.last_name}
                      </Link>
                    ) : (m.from_email || m.subject || '—')}
                  </div>
                  <div className="text-xs text-muted-foreground line-clamp-2">{m.subject || m.body}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
